import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Guid, IDeleteEntity, IGenericResponse } from '../../shared';
import { LoadingService } from '../../shared/loading';
import { LoggingService } from '../../shared/services/logging/logging.service';
import * as fromDataServices from '../../data-services';
import * as fromRoot from '../../store';
import moment from 'moment';
import {
    SyncGeneralLedgerResponseModel,
    PayrollCode,
    ThirdPartyPayroll
} from './store';
import { ReimbursementPaycode } from './store/reimbursement-paycode.model';
import { FileFormatModel } from './store/file-format.model';
@Injectable({
    providedIn: 'root'
})
export class ThirdPartyPayrollService extends LoadingService {
    private uri = `${environment.apiLocation}/ThirdPartyPayroll`;
    private uriUpload = `${environment.apiLocation}/PayrollExport/UploadPayroll`;
    private uriPayrollExport = `${environment.apiLocation}/PayrollExport`;
    private uriUpdatePayrollExportStatus = `${environment.apiLocation}/PayrollExport/UpdateStatus`;
    private uriSyncGL = `${environment.apiLocation}/PayrollExport/SyncGL`;
    private uriPayrollCode = `${environment.apiLocation}/PayrollCode`;
    private uriFileFormat = `${environment.apiLocation}/PayrollExportFormat`;
    private uriReimbursementPaycodes = `${environment.apiLocation}/PayrollReimbursementCodes`;

    constructor(
        private http: HttpClient,
        protected rootStore: Store<fromRoot.CoreState>,
        private log: LoggingService
    ) {
        super(rootStore);
    }

    public get(): Observable<ThirdPartyPayroll[]> {
        super.Load();
        return this.http.get<ThirdPartyPayroll[]>(this.uri).pipe(
            tap(data => {
                super.Stop();
            }),
            map(items =>
                items.map(x => ({
                    ...x,
                    // Status: x.Status || {
                    //     StatusCode: null,
                    //     Message: null
                    // },
                    id: this.getThirdPartyPayrollId(x)
                }))
            )
        );
    }
    public put(
        items: ThirdPartyPayroll[]
    ): Observable<IGenericResponse<ThirdPartyPayroll>> {
        super.Load();
        return this.http
            .put<IGenericResponse<ThirdPartyPayroll>>(this.uri, {
                ThirdPartyPayrollItems: items
            })
            .pipe(
                tap(() => {
                    super.Stop();
                }),
                map(resp => ({
                    ...resp,
                    Items: resp.Items.map(x => ({
                        ...x,
                        // Status: x.Status || {
                        //     StatusCode: null,
                        //     Message: null
                        // },
                        id: this.getThirdPartyPayrollId(x)
                    }))
                }))
            );
    }
    public getPayrollExport(
        startDate: Date,
        endDate: Date,
        codeAsRow: boolean
    ) {
        super.Load();
        return this.http
            .post<any[]>(this.uriPayrollExport, {
                Startdate: startDate,
                Enddate: endDate,
                Codeasrow: codeAsRow ? 1 : 0
            })
            .pipe(
                // map(resp => JSON.parse(resp)),
                tap(resp => {
                    super.Stop();
                    this.log.verbose({
                        type: 'ThirdPartyPayrollService:getPayrollExport',
                        resp
                    });
                }),
                map(resp =>
                    resp.map(x => ({
                        ...x,
                        id: this.getThirdPartyPayrollId(x)
                    }))
                )
            );
    }
    public fetchPayrollExportStatus(
        id: string | number
    ): Observable<fromDataServices.PayrollExport.PayrollExportModel> {
        return this.http
            .get<fromDataServices.PayrollExport.PayrollExportModel>(
                `${this.uriUpdatePayrollExportStatus}/${id}`
            )
            .pipe(
                tap(() => {
                    super.Stop();
                })
            );
    }

    public sendPayroll(endDate: Date): Observable<any> {
        return this.http
            .post(
                `${this.uriUpload}/${moment(endDate).format('YYYY-MM-DD')}`,
                null
            )
            .pipe(
                tap(() => {
                    super.Stop();
                })
            );
    }

    public getPayrollCodes(): Observable<PayrollCode[]> {
        // return of((payrollCodeData as any).default);
        super.Load();
        return this.http.get<PayrollCode[]>(this.uriPayrollCode).pipe(
            tap(() => {
                super.Stop();
            }),
            map(items =>
                items.map(x => ({
                    ...x,
                    // Status: x.Status || {
                    //     StatusCode: null,
                    //     Message: null
                    // },
                    id: this.getPayrollCodeId(x)
                }))
            )
        );
    }
    public getReimbursementPaycodes(): Observable<ReimbursementPaycode[]> {
        super.Load();
        return this.http
            .get<ReimbursementPaycode[]>(this.uriReimbursementPaycodes)
            .pipe(
                tap(() => {
                    super.Stop();
                })
            );
    }
    public putPayrollCodes(
        items: PayrollCode[]
    ): Observable<IGenericResponse<PayrollCode>> {
        super.Load();
        return this.http
            .put<IGenericResponse<PayrollCode>>(this.uriPayrollCode, {
                PayrollCodeList: items
            })
            .pipe(
                tap(() => {
                    super.Stop();
                }),
                map(resp => ({
                    ...resp,
                    Items: resp.Items.map(x => ({
                        ...x,
                        // Status: x.Status || {
                        //     StatusCode: null,
                        //     Message: null
                        // },
                        id: this.getPayrollCodeId(x)
                    }))
                }))
            );
    }
    public deletePayrollCode(paycodeId: number) {
        return this.http.request<IGenericResponse<IDeleteEntity>>(
            'DELETE',
            this.uriPayrollCode,
            {
                body: { EntityIDs: [paycodeId] }
            }
        );
    }
    public getFileFormat(): Observable<FileFormatModel[]> {
        super.Load();
        return this.http.get<FileFormatModel[]>(this.uriFileFormat).pipe(
            tap(() => {
                super.Stop();
            }),
            map(items =>
                items.map(x => ({
                    ...x,
                    id: this.getFileFormatId(x)
                }))
            )
        );
    }
    public putFileFormats(
        items: FileFormatModel[]
    ): Observable<IGenericResponse<FileFormatModel>> {
        super.Load();
        return this.http
            .put<IGenericResponse<FileFormatModel>>(this.uriFileFormat, {
                PayrollExportFormatList: items
            })
            .pipe(
                tap(() => {
                    super.Stop();
                }),
                map(resp => ({
                    ...resp,
                    Items: resp.Items.map(x => ({
                        ...x,
                        // Status: x.Status || {
                        //     StatusCode: null,
                        //     Message: null
                        // },
                        id: this.getFileFormatId(x)
                    }))
                }))
            );
    }
    private getFileFormatId(item: FileFormatModel) {
        return item.Fieldid;
    }
    private getThirdPartyPayrollId(item: ThirdPartyPayroll) {
        return Guid.newGuid();
    }
    private getPayrollCodeId(item: PayrollCode) {
        return item.Paycodeid.toString();
    }
    public syncGL(ppe: Date): Observable<SyncGeneralLedgerResponseModel> {
        super.Load();
        return this.http
            .get<SyncGeneralLedgerResponseModel>(
                `${this.uriSyncGL}/${moment(ppe).format('YYYY-MM-DD')}`
            )
            .pipe(
                tap(resp => {
                    super.Stop();
                    this.log.verbose({
                        type: 'ThirdPartyPayrollService:SyncGL',
                        ppe,
                        resp
                    });
                }),
                catchError((e, c) => {
                    super.Stop();
                    throw e;
                })
            );
    }
}
